import { useEffect } from 'react';

import { FilterDropdown } from '@/components/filters/FilterDropdown';

import { useTenantWebApplications } from './useTenantWebApplications';

export type TrafficSource = 'guard' | 'file';

interface DynamicTrafficFieldsProps {
  /** Whether dynamic-traffic enrichment is enabled (checkbox state). */
  enabled: boolean;
  /** Which traffic source is selected when enabled. */
  source: TrafficSource;
  /** Selected WebApplication keys (guard source). */
  selectedWebApps: string[];
  /** The uploaded file's storage key (file source), or null when none. */
  uploadedFileKey: string | null;
  /** OSINT mode — forwarded to the WebApplication query. */
  osint: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onSourceChange: (source: TrafficSource) => void;
  onSelectedWebAppsChange: (keys: string[]) => void;
  onFileUpload: (file: File) => void;
}

/**
 * Presentation-only fields for opt-in dynamic-traffic enrichment in the
 * Constantine modal. State is lifted to the parent modal; this component only
 * renders the checkbox + source radio + (guard) WebApplication picker or
 * (file) traffic-file upload, and forwards changes via callbacks.
 *
 * The WebApplication picker reuses the shared `FilterDropdown` (built-in
 * search, virtualization, Select-all/Deselect-all, count UX, accessibility).
 * `maxSelections` is omitted so Select-all stays enabled.
 *
 * Native checkbox/radio/file inputs are used deliberately — only `<select>` is
 * banned by `react/forbid-elements`; these match the sibling Constantine fields.
 */
export function DynamicTrafficFields({
  enabled,
  source,
  selectedWebApps,
  uploadedFileKey,
  osint,
  onEnabledChange,
  onSourceChange,
  onSelectedWebAppsChange,
  onFileUpload,
}: DynamicTrafficFieldsProps) {
  const { webApps, status } = useTenantWebApplications({ osint });
  const guardUnavailable = status === 'success' && webApps.length === 0;

  // Auto-switch off the unusable default: when there's no Guard-collected
  // traffic, land the user on the working "Upload a traffic file" path.
  useEffect(() => {
    if (guardUnavailable && source === 'guard') {
      onSourceChange('file');
    }
  }, [guardUnavailable, source]);

  return (
    <div className="space-y-3">
      <label className="text-chariot-text-primary flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => onEnabledChange(e.target.checked)}
        />
        <span>Enrich review with dynamic traffic</span>
      </label>

      {enabled && (
        <div className="border-chariot-stroke space-y-3 rounded-md border p-3">
          <label className="text-chariot-text-primary flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="constantine-traffic-source"
              checked={source === 'guard'}
              disabled={guardUnavailable}
              onChange={() => onSourceChange('guard')}
            />
            <span>Use Guard-collected traffic</span>
          </label>
          {guardUnavailable && (
            <p className="text-chariot-text-secondary text-xs">
              No collected traffic for this tenant — upload a file instead.
            </p>
          )}

          {source === 'guard' && (
            <div className="pl-6">
              <FilterDropdown
                id="traffic-webapps"
                label="Web applications"
                options={webApps.map(w => ({
                  id: w.key,
                  label: w.label,
                  value: w.key,
                }))}
                selectedValues={selectedWebApps}
                isLoadingOptions={status === 'pending'}
                onApply={opts =>
                  onSelectedWebAppsChange(opts.map(o => String(o.value)))
                }
                onClear={() => onSelectedWebAppsChange([])}
              />
            </div>
          )}

          <label className="text-chariot-text-primary flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="constantine-traffic-source"
              checked={source === 'file'}
              onChange={() => onSourceChange('file')}
            />
            <span>Upload a traffic file</span>
          </label>

          {source === 'file' && (
            <div className="pl-6">
              <label
                htmlFor="constantine-traffic-file"
                className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary hover:border-chariot-brand-primary inline-flex cursor-pointer items-center rounded-md border px-3 py-1.5 text-sm font-medium"
              >
                Choose file
              </label>
              <input
                id="constantine-traffic-file"
                type="file"
                accept=".har,.json,.xml"
                aria-label="Traffic file"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) onFileUpload(file);
                }}
                className="sr-only"
              />
              {uploadedFileKey && (
                <p className="text-chariot-text-secondary mt-1 text-xs">
                  Uploaded: {uploadedFileKey}
                </p>
              )}
              <p className="text-chariot-text-secondary mt-1 text-xs">
                Burp Suite XML or HAR.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
